import { FC, useEffect, useRef, useState } from "react";
import EuropeaLogo from "../EuropeaLogo";
import LanguageSwitcher from "../LanguageSwitcher";
import { useLocale } from "../i18n/locale";
import { useReservation } from "./reservationContext";

export interface OnboardingTopBarProps {
  className?: string;
}

// The reservations desk the "Need help?" sheet points the tenant at.
const SUPPORT_EMAIL = "reservation@europea-residences.com";

/**
 * The workspace's own top bar. Unlike OnboardingHeader (the phone steps') this
 * runs the full width of the viewport and carries no back control — going back
 * belongs to the step footer, next to the action it reverses.
 *
 * On a phone the preview marker drops away, leaving the lockup, the language
 * switch and the help button — the latter collapsing to a round "?" so the
 * label does not crowd the bar.
 */
const OnboardingTopBar: FC<OnboardingTopBarProps> = ({ className = "" }) => {
  const { t } = useLocale();
  const reservation = useReservation();
  const [helpOpen, setHelpOpen] = useState(false);
  const helpRef = useRef<HTMLDivElement>(null);

  // Close the help sheet on an outside click or Escape.
  useEffect(() => {
    if (!helpOpen) return;
    const onDown = (e: MouseEvent) => {
      if (helpRef.current && !helpRef.current.contains(e.target as Node)) {
        setHelpOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setHelpOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [helpOpen]);

  const mailtoHref =
    `mailto:${SUPPORT_EMAIL}` +
    `?subject=${encodeURIComponent(`Onboarding help — ${reservation.bookingReference}`)}` +
    `&body=${encodeURIComponent(`Booking reference: ${reservation.bookingReference}\n\n`)}`;

  return (
    <header
      className={`flex h-[70px] items-center justify-between border-b border-[#ebe9e4] bg-white px-5 min-[760px]:h-[84px] min-[760px]:px-7 min-[1100px]:px-[42px] ${className}`}
    >
      <EuropeaLogo className="h-[42px] text-[14px] min-[760px]:h-[46px] min-[760px]:text-[15px]" />

      <div className="flex items-center gap-1 min-[760px]:gap-4">
        <LanguageSwitcher />

        <div ref={helpRef} className="relative block">
          <button
            type="button"
            onClick={() => setHelpOpen((o) => !o)}
            aria-haspopup="dialog"
            aria-expanded={helpOpen}
            aria-label={t.desktop.needHelp}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-[#d8d3c9] text-[15px] font-bold text-navy transition-colors hover:border-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-offset-2 min-[760px]:h-auto min-[760px]:w-auto min-[760px]:rounded-[5px] min-[760px]:px-[18px] min-[760px]:py-[11px] min-[760px]:text-[13px]"
          >
            {/* A "?" glyph on a phone where the label would crowd the bar; the
                full label returns on desktop. */}
            <span className="min-[760px]:hidden" aria-hidden="true">?</span>
            <span className="hidden min-[760px]:inline">{t.desktop.needHelp}</span>
          </button>

          {helpOpen && (
            <div
              role="dialog"
              aria-label={t.desktop.helpTitle}
              className="absolute right-0 top-[calc(100%+12px)] z-50 w-[320px] rounded-[10px] border border-[#ece8e1] bg-white p-5 text-left shadow-[0_18px_44px_rgba(20,38,63,0.16)]"
            >
              <div className="mb-2 flex items-start justify-between gap-3">
                <strong className="font-serif text-[17px] font-normal text-navy">
                  {t.desktop.helpTitle}
                </strong>
                <button
                  type="button"
                  aria-label={t.desktop.helpClose}
                  onClick={() => setHelpOpen(false)}
                  className="-mr-1 -mt-1 grid h-7 w-7 flex-none place-items-center rounded-full text-[18px] leading-none text-[#8a857d] transition-colors hover:bg-[#f3f0ea] hover:text-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-navy"
                >
                  ✕
                </button>
              </div>

              <p className="text-[12.5px] leading-relaxed text-[#5c6068]">
                {t.desktop.helpBody}
              </p>
              <p className="mt-3 text-[12.5px] leading-relaxed text-[#5c6068]">
                {t.desktop.helpReplyTime}
              </p>

              <a
                href={mailtoHref}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 flex items-center gap-2 text-[12.5px] font-semibold text-[#1F4FA8] hover:underline"
              >
                <svg
                  className="h-4 w-4 flex-none text-[#946e3b]"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.6}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <rect x="3" y="5" width="18" height="14" rx="2" />
                  <path d="m3 7 9 6 9-6" />
                </svg>
                {SUPPORT_EMAIL}
              </a>

              <a
                href={mailtoHref}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 block rounded-[6px] bg-navy py-3 text-center text-[13px] font-bold text-white transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-offset-2"
              >
                {t.desktop.helpEmailCta}
              </a>

              <p className="mt-3 text-center text-[11px] leading-relaxed text-[#a29b90]">
                {t.desktop.helpReference}{" "}
                <strong className="font-bold text-navy">
                  {reservation.bookingReference}
                </strong>
              </p>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default OnboardingTopBar;
